import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Eye,
  Trash2,
  X,
  Mail,
  Phone,
  Clock,
  MessageSquare,
  Tag,
  Reply,
} from 'lucide-react';
import { db } from '../../services/firebase';
import { collection, updateDoc, deleteDoc, doc, onSnapshot, query, orderBy } from 'firebase/firestore';

export default function AdminContacts() {
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (!db) return;

    const q = query(collection(db, 'contacts'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const contactsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setContacts(contactsData);
    });

    return unsubscribe;
  }, []);

  const handleMarkAsRead = async (contactId) => {
    try {
      await updateDoc(doc(db, 'contacts', contactId), {
        read: true,
        readAt: new Date(),
      });
    } catch (error) {
      console.error('Error marking contact as read:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;

    try {
      await deleteDoc(doc(db, 'contacts', id));
      setSelectedContact(null);
    } catch (error) {
      console.error('Error deleting contact:', error);
      alert('Failed to delete contact');
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'general':
        return 'bg-blue-500';
      case 'booking':
        return 'bg-orange-500';
      case 'press':
        return 'bg-purple-500';
      case 'business':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  const filteredContacts = contacts.filter((contact) => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !contact.read;
    if (filter === 'read') return contact.read;
    return contact.category === filter;
  });

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const stats = {
    total: contacts.length,
    unread: contacts.filter((c) => !c.read).length,
    general: contacts.filter((c) => c.category === 'general').length,
    booking: contacts.filter((c) => c.category === 'booking').length,
    press: contacts.filter((c) => c.category === 'press').length,
    business: contacts.filter((c) => c.category === 'business').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white">Contact Messages</h2>
        <p className="text-gray-400">Manage inquiries from the contact form</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card
          className={`cursor-pointer transition-all ${filter === 'all' ? 'border-orange-500' : ''}`}
          onClick={() => setFilter('all')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <MessageSquare className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.total}</p>
                <p className="text-xs text-gray-400">Total Messages</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-all ${filter === 'unread' ? 'border-yellow-500' : ''}`}
          onClick={() => setFilter('unread')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/20 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.unread}</p>
                <p className="text-xs text-gray-400">Unread</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-all ${filter === 'booking' ? 'border-orange-500' : ''}`}
          onClick={() => setFilter('booking')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <Tag className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.booking}</p>
                <p className="text-xs text-gray-400">Booking</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-all ${filter === 'press' ? 'border-purple-500' : ''}`}
          onClick={() => setFilter('press')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <Tag className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.press}</p>
                <p className="text-xs text-gray-400">Press</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Messages List */}
      <div className="space-y-4">
        {filteredContacts.map((contact) => (
          <motion.div
            key={contact.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card
              className={`hover:border-orange-500/50 transition-all cursor-pointer ${
                !contact.read ? 'border-l-4 border-l-orange-500' : ''
              }`}
              onClick={() => {
                setSelectedContact(contact);
                if (!contact.read) handleMarkAsRead(contact.id);
              }}
            >
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    {/* Avatar */}
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-lg">
                        {contact.name?.[0]?.toUpperCase() || '?'}
                      </span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-white">{contact.name}</h3>
                        <span
                          className={`px-2 py-0.5 text-xs font-semibold rounded capitalize text-white ${getCategoryColor(
                            contact.category
                          )}`}
                        >
                          {contact.category || 'general'}
                        </span>
                        {!contact.read && (
                          <span className="px-2 py-0.5 text-xs font-semibold rounded bg-yellow-500 text-white">
                            New
                          </span>
                        )}
                      </div>
                      <p className="text-orange-400 font-medium">{contact.subject}</p>
                      <p className="text-gray-400 text-sm mt-1 line-clamp-2">{contact.message}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {contact.email}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(contact.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 md:flex-shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedContact(contact);
                        if (!contact.read) handleMarkAsRead(contact.id);
                      }}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(contact.id);
                      }}
                      className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}

        {filteredContacts.length === 0 && (
          <div className="text-center py-12">
            <MessageSquare className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No messages found</h3>
            <p className="text-gray-400">
              {filter === 'all'
                ? 'Contact messages will appear here'
                : `No ${filter} messages`}
            </p>
          </div>
        )}
      </div>

      {/* Message Detail Modal */}
      {selectedContact && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gray-900 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-gray-900 p-6 border-b border-gray-800 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold">
                    {selectedContact.name?.[0]?.toUpperCase() || '?'}
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">{selectedContact.name}</h3>
                  <span
                    className={`inline-block px-2 py-0.5 text-xs font-semibold rounded capitalize text-white ${getCategoryColor(
                      selectedContact.category
                    )}`}
                  >
                    {selectedContact.category || 'general'}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelectedContact(null)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Contact Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
                  <Mail className="w-5 h-5 text-orange-500" />
                  <div>
                    <p className="text-xs text-gray-400">Email</p>
                    <a
                      href={`mailto:${selectedContact.email}`}
                      className="text-white font-medium hover:text-orange-400"
                    >
                      {selectedContact.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
                  <Phone className="w-5 h-5 text-orange-500" />
                  <div>
                    <p className="text-xs text-gray-400">Phone</p>
                    {selectedContact.phone ? (
                      <a
                        href={`tel:${selectedContact.phone}`}
                        className="text-white font-medium hover:text-orange-400"
                      >
                        {selectedContact.phone}
                      </a>
                    ) : (
                      <p className="text-gray-500">Not provided</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Subject */}
              <div>
                <h4 className="text-sm font-semibold text-gray-400 uppercase mb-2">Subject</h4>
                <p className="text-white font-medium text-lg">{selectedContact.subject}</p>
              </div>

              {/* Message */}
              <div>
                <h4 className="text-sm font-semibold text-gray-400 uppercase mb-2">Message</h4>
                <div className="p-4 bg-gray-800 rounded-lg">
                  <p className="text-gray-300 whitespace-pre-wrap">{selectedContact.message}</p>
                </div>
              </div>

              {/* Timestamp */}
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Clock className="w-4 h-4" />
                Received: {formatDate(selectedContact.createdAt)}
              </div>

              {/* Actions */}
              <div className="flex gap-4 pt-4 border-t border-gray-800">
                <a
                  href={`mailto:${selectedContact.email}?subject=Re: ${selectedContact.subject}`}
                  className="flex-1"
                >
                  <Button className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700">
                    <Reply className="w-4 h-4 mr-2" />
                    Reply via Email
                  </Button>
                </a>
                <Button
                  variant="outline"
                  onClick={() => handleDelete(selectedContact.id)}
                  className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
